import { Button } from "@fieldwork/ui/components/button";
import { Input } from "@fieldwork/ui/components/input";
import { cn } from "@fieldwork/ui/lib/utils";
import { Link, createFileRoute } from "@tanstack/react-router";
import cytoscape, { type Core, type ElementDefinition, type StylesheetJson } from "cytoscape";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import z from "zod";

import { NotConnected, useServer } from "@/components/server-hooks";
import { Chip } from "@/components/ui-bits";
import { useAgentQuery } from "@/lib/act";
import { useOpenPlane } from "@/lib/plane";

export const Route = createFileRoute("/_auth/servers/$id/graph")({
  validateSearch: z.object({ focus: z.string().optional() }),
  component: Graph,
});

// The graph is built on the server by task-manager-ai's ui/graph.py; this page ports its console view.
type GNode = {
  id: string;
  type: "me" | "project" | "org" | "person";
  label: string;
  project?: string | null;
  plane?: string | null;
  plane_id?: string | null;
  subtitle?: string;
  group?: boolean;
  size?: number;
  org?: string;
  confirmed?: boolean;
  confidence?: string | null;
  detail?: Record<string, string[]>;
  projects?: string[];
  hints?: string[];
};
type GEdge = { source: string; target: string; kind: "me" | "member" | "project" | "hint" | "org-project" };
type GraphData = { nodes: GNode[]; edges: GEdge[]; workspace: string | null };

const PALETTE = ["#FF4F1A", "#7DA7F0", "#5FBF7F", "#F0B65A", "#C792EA", "#4FC1C9", "#F48A80", "#8C8981"];
/** lighter text tint for each palette colour, for the project toggles (as on the canvas) */
const TINT: Record<string, string> = { "#FF4F1A": "#FF8A63", "#7DA7F0": "#A7C2F7", "#5FBF7F": "#7FD39B", "#C792EA": "#D9B3F2", "#4FC1C9": "#86D8DE" };
const FG = "#ECEAE4";
const MUTED = "#8C8981";
const KIND: Record<GNode["type"], string> = { me: "you", project: "project", org: "organisation", person: "person" };

const STYLE: StylesheetJson = [
  {
    selector: "node",
    style: {
      "background-color": "data(color)",
      label: "data(label)",
      color: FG,
      "font-family": "Geist Variable, system-ui, sans-serif",
      "font-size": 11,
      "text-valign": "bottom",
      "text-margin-y": 4,
      "text-wrap": "ellipsis",
      "text-max-width": "130",
      width: 14,
      height: 14,
      "text-background-color": "#111111",
      "text-background-opacity": 0.75,
      "text-background-padding": "1",
      "min-zoomed-font-size": 7,
      "transition-property": "opacity",
      "transition-duration": 0.15,
    },
  },
  { selector: 'node[type="me"]', style: { width: 44, height: 44, "font-size": 13, "font-weight": 600 } },
  { selector: 'node[type="project"]', style: { shape: "hexagon", width: 36, height: 36, "font-size": 12, "font-weight": 600 } },
  { selector: 'node[type="org"]', style: { shape: "round-rectangle", width: 26, height: 18 } },
  {
    selector: "node[group=1]",
    style: { "background-opacity": 0.35, "border-width": 1.5, "border-style": "dashed", "border-color": "data(color)" },
  },
  { selector: "node[ring=1]", style: { "border-width": 2.5, "border-color": "#5FBF7F" } },
  {
    selector: ":parent",
    style: {
      "background-opacity": 0.07,
      "border-width": 1.5,
      "border-color": "data(color)",
      "text-valign": "top",
      "text-halign": "center",
      "text-margin-y": -4,
      padding: "10",
      shape: "round-rectangle",
      "font-family": "JetBrains Mono Variable, ui-monospace, monospace",
      "font-size": 12,
      "font-weight": 700,
      "min-zoomed-font-size": 5,
      "text-wrap": "none",
      "text-max-width": "4000",
    },
  },
  { selector: ':parent[type="org"]', style: { "font-size": 22 } },
  {
    selector: ':parent[type="project"]',
    style: { "font-size": 60, "border-width": 3, padding: "20", "background-opacity": 0.05, "text-margin-y": -8 },
  },
  { selector: 'node[type="person"]', style: { "text-max-width": "105" } },
  { selector: "node:selected", style: { "border-width": 3, "border-color": "#FF4F1A", "font-weight": 700, "font-size": 13, "z-index": 10 } },
  { selector: 'node[type="person"]:selected', style: { width: 24, height: 24 } },
  { selector: "edge", style: { width: 1, "line-color": "#4A4945", "curve-style": "straight", opacity: 0.9 } },
  { selector: 'edge[kind="hint"]', style: { "line-style": "dashed" } },
  { selector: ".faded", style: { opacity: 0.12 } },
  { selector: ".hl", style: { "border-width": 3, "border-color": "#FF4F1A" } },
];

// ---- deterministic packing (same as the console): people in a grid inside their organisation,
// organisations in rows inside their project (largest first), projects side by side. Only leaf
// positions are set; the boxes (compound parents) size themselves around their children.
const CELL_W = 120;
const CELL_H = 48;
const ORG_PAD = 34;
const PROJ_PAD = 70;
const GAP = 26;
type Block = { id: string; w: number; h: number; x?: number; y?: number };

function shelf<T extends Block>(items: T[], targetW: number) {
  let x = 0;
  let y = 0;
  let rowH = 0;
  let W = 0;
  for (const it of items) {
    if (x > 0 && x + it.w > targetW) {
      x = 0;
      y += rowH + GAP;
      rowH = 0;
    }
    it.x = x;
    it.y = y;
    x += it.w + GAP;
    rowH = Math.max(rowH, it.h);
    W = Math.max(W, x - GAP);
  }
  return { w: W, h: y + rowH };
}

function mapPositions(cy: Core) {
  const pos: Record<string, { x: number; y: number }> = {};
  type OrgBlock = Block & { kids: cytoscape.NodeCollection; cols: number; parent: string };
  type ProjBlock = Block & { blocks: OrgBlock[]; single?: boolean };
  const byParent: Record<string, OrgBlock[]> = {};
  cy.nodes('[type="org"]').forEach((org) => {
    const kids = org.children().sort((a, b) => String(a.data("label")).localeCompare(String(b.data("label"))));
    const cols = Math.max(1, Math.ceil(Math.sqrt(kids.length * 1.8)));
    const rows = Math.ceil(kids.length / cols);
    const b: OrgBlock = {
      id: org.id(),
      kids,
      cols,
      w: cols * CELL_W + ORG_PAD,
      h: rows * CELL_H + ORG_PAD + 18,
      parent: org.data("parent") || "_none",
    };
    (byParent[b.parent] ??= []).push(b);
  });
  const projBlocks: ProjBlock[] = [];
  for (const [pid, blocks] of Object.entries(byParent)) {
    blocks.sort((a, b) => b.w * b.h - a.w * a.h);
    const area = blocks.reduce((t, b) => t + (b.w + GAP) * (b.h + GAP), 0);
    const size = shelf(blocks, Math.max(Math.max(...blocks.map((b) => b.w)), Math.sqrt(area) * 1.4));
    projBlocks.push({ id: pid, blocks, w: size.w + PROJ_PAD, h: size.h + PROJ_PAD + 40 });
  }
  cy.nodes('[type="project"]')
    .filter((n) => n.isChildless())
    .forEach((pn) => {
      projBlocks.push({ id: pn.id(), blocks: [], w: 220, h: 160, single: true });
    });
  if (!projBlocks.length) return pos;
  projBlocks.sort((a, b) => b.w * b.h - a.w * a.h);
  const total = projBlocks.reduce((t, b) => t + (b.w + GAP) * (b.h + GAP), 0);
  const aspect = Math.max(0.6, Math.min(2.5, cy.width() / Math.max(1, cy.height()))); // fill the canvas shape
  shelf(projBlocks, Math.max(Math.max(...projBlocks.map((b) => b.w)), Math.sqrt(total * aspect)));
  for (const pb of projBlocks) {
    if (pb.single) pos[pb.id] = { x: pb.x! + pb.w / 2, y: pb.y! + pb.h / 2 };
    for (const ob of pb.blocks)
      ob.kids.forEach((k, i) => {
        pos[k.id()] = {
          x: pb.x! + PROJ_PAD / 2 + ob.x! + ORG_PAD / 2 + (i % ob.cols) * CELL_W + CELL_W / 2,
          y: pb.y! + PROJ_PAD / 2 + 40 + ob.y! + ORG_PAD / 2 + 18 + Math.floor(i / ob.cols) * CELL_H + CELL_H / 2,
        };
      });
  }
  return pos;
}

function settle(cy: Core) {
  // fit, but never blow a handful of nodes up past ~1:1
  cy.fit(cy.elements(), 40);
  if (cy.zoom() > 1.1) {
    cy.zoom(1.1);
    cy.center();
  }
}

function highlight(cy: Core, id: string, center: boolean) {
  cy.elements().removeClass("faded hl");
  const n = cy.getElementById(id);
  if (!n.length) return;
  const keep = n.closedNeighborhood().union(n.ancestors()).union(n.descendants());
  cy.elements().not(keep).addClass("faded");
  n.addClass("hl");
  cy.$(":selected").unselect();
  n.select();
  if (center) {
    // frame the node itself (and what it contains), not its whole project
    const frame = n.union(n.descendants()).union(n.connectedEdges().connectedNodes());
    cy.animate({ fit: { eles: frame, padding: 50 }, duration: 300 });
    setTimeout(() => {
      if (cy.zoom() > 1.6) cy.zoom({ level: 1.6, position: n.position() });
    }, 320);
  }
}

function GraphBody() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const connected = !!useServer(id).data?.hub.connected;
  const q = useAgentQuery<GraphData>(id, "graph_data", {}, connected);
  const openPlane = useOpenPlane(id);
  const G = q.data;

  const byId = useMemo(() => new Map((G?.nodes ?? []).map((n) => [n.id, n])), [G]);
  const colors = useMemo(() => {
    const c: Record<string, string> = {};
    (G?.nodes ?? []).filter((n) => n.type === "project").forEach((n, i) => (c[n.project!] = PALETTE[i % PALETTE.length]!));
    return c;
  }, [G]);
  const [off, setOff] = useState<Set<string>>(new Set());
  const [noGroups, setNoGroups] = useState(false);
  const [sure, setSure] = useState(false);
  const [focus, setFocus] = useState<{ id: string; center: boolean } | null>(search.focus ? { id: search.focus, center: true } : null);
  const [panelId, setPanelId] = useState(search.focus ?? "me");
  const [findText, setFindText] = useState("");
  const box = useRef<HTMLDivElement>(null);
  const findRef = useRef<HTMLInputElement>(null);
  const cyRef = useRef<Core | null>(null);
  const focusRef = useRef(focus);
  focusRef.current = focus;

  const hidden = useCallback(
    (n: GNode | undefined): boolean => {
      if (!n) return true;
      if (n.type !== "me" && n.project && off.has(n.project)) return true;
      if (noGroups && n.type === "org" && n.group) return true;
      if (noGroups && n.type === "person" && byId.get(n.org!)?.group) return true;
      if (sure && n.type === "person" && !n.confirmed && n.confidence !== "high") return true;
      return false;
    },
    [off, noGroups, sure, byId],
  );

  const select = useCallback((nid: string, center: boolean) => {
    setFocus({ id: nid, center });
    setPanelId(nid);
  }, []);
  const go = useCallback(
    (nid: string) => {
      if (cyRef.current?.getElementById(nid).length) select(nid, true);
      else setPanelId(nid); // e.g. "you", or hidden by a filter
    },
    [select],
  );
  const reset = useCallback(() => {
    setFocus(null);
    setPanelId("me");
    const cy = cyRef.current;
    if (cy) {
      cy.elements().removeClass("faded hl");
      cy.$(":selected").unselect();
      settle(cy);
    }
  }, []);

  // create the renderer once the data is here
  useEffect(() => {
    if (!G || !box.current) return;
    const cy = cytoscape({ container: box.current, style: STYLE, minZoom: 0.08, maxZoom: 3, boxSelectionEnabled: false });
    cyRef.current = cy;
    cy.on("tap", "node", (ev) => select(ev.target.id(), false));
    cy.on("dbltap", "node", (ev) => select(ev.target.id(), true));
    cy.on("tap", (ev) => {
      if (ev.target === cy) reset();
    });
    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [G, select, reset]);

  // (re)draw the full map whenever the data or a filter changes
  useEffect(() => {
    const cy = cyRef.current;
    if (!G || !cy) return;
    cy.elements().remove();
    const nodeData = (n: GNode, parent?: string): ElementDefinition => ({
      group: "nodes",
      data: {
        id: n.id,
        label: n.type === "org" ? `${n.label} (${n.size})` : n.label,
        type: n.type,
        color: n.type === "me" ? FG : (colors[n.project ?? ""] ?? MUTED),
        group: n.group ? 1 : 0,
        ring: n.confirmed ? 1 : 0,
        ...(parent ? { parent } : {}),
      },
    });
    const els: ElementDefinition[] = [];
    for (const n of G.nodes) if (n.type === "project" && !hidden(n)) els.push(nodeData(n));
    for (const n of G.nodes)
      if (n.type === "org" && !hidden(n)) els.push(nodeData(n, n.project && !off.has(n.project) ? `project:${n.project}` : undefined));
    for (const n of G.nodes) if (n.type === "person" && !hidden(n) && !hidden(byId.get(n.org!))) els.push(nodeData(n, n.org));
    cy.add(els);
    // containment shows org and project membership; draw only links that cross it
    for (const e of G.edges) {
      if (e.kind === "member" || e.kind === "org-project" || e.kind === "me") continue;
      const s = byId.get(e.source);
      const t = byId.get(e.target);
      if (e.kind === "project" && byId.get(s?.org ?? "")?.project === t?.project) continue;
      if (cy.getElementById(e.source).length && cy.getElementById(e.target).length)
        cy.add({ group: "edges", data: { id: `${e.source}>${e.target}>${e.kind}`, source: e.source, target: e.target, kind: e.kind } });
    }
    cy.layout({ name: "preset", positions: mapPositions(cy), fit: false }).run();
    settle(cy);
    const f = focusRef.current;
    if (f && cy.getElementById(f.id).length) highlight(cy, f.id, false);
  }, [G, off, hidden, byId, colors]);

  // highlight the focused node and keep it in the URL
  useEffect(() => {
    const cy = cyRef.current;
    if (!G || !cy) return;
    if (focus) highlight(cy, focus.id, focus.center);
    if ((focus?.id ?? undefined) !== search.focus) navigate({ search: { focus: focus?.id }, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, G]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const t = ev.target as HTMLElement;
      if (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT") {
        if (ev.key === "Escape") t.blur();
        return;
      }
      if (ev.key === "/") {
        ev.preventDefault();
        findRef.current?.focus();
      } else if (ev.key === "Escape") reset();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [reset]);

  const find = (text: string) => {
    const v = text.trim().toLowerCase();
    const input = findRef.current;
    if (!v || !G || !input) return;
    const hit =
      G.nodes.find((n) => n.label.toLowerCase() === v) ??
      G.nodes.find((n) => n.label.toLowerCase().startsWith(v)) ??
      G.nodes.find((n) => n.label.toLowerCase().includes(v) || JSON.stringify(n.detail ?? {}).toLowerCase().includes(v));
    input.setCustomValidity("");
    if (!hit) input.setCustomValidity("No match");
    else if (hit.type !== "me" && !cyRef.current?.getElementById(hit.id).length)
      input.setCustomValidity(`${hit.label} is hidden by a filter or a project toggle`);
    if (!input.checkValidity()) {
      input.reportValidity();
      return;
    }
    go(hit!.id);
  };

  const message = !connected ? (
    <NotConnected />
  ) : q.error ? (
    <p className="text-sm text-[#F48A80]">{q.error.message}</p>
  ) : !G ? (
    <p className="text-sm text-[#A8A59D]">Building the graph…</p>
  ) : G.nodes.length <= 1 ? (
    <p className="text-sm text-[#A8A59D]">Nothing to draw yet: the graph fills in as the agent learns who you work with.</p>
  ) : null;
  if (message || !G)
    return (
      <>
        <GraphHeader />
        <div className="px-5 pt-6 pb-16 sm:px-10">{message}</div>
      </>
    );
  const projects = G.nodes.filter((n) => n.type === "project");

  return (
    <>
      <GraphHeader
        actions={
          <>
            <form
              className="flex w-80 max-w-full"
              onSubmit={(e) => {
                e.preventDefault();
                find(findText);
              }}
            >
              <label className="flex w-full">
                <span className="sr-only">Find in graph</span>
                <Input
                  ref={findRef}
                  type="search"
                  list="graph-names"
                  className="h-10 bg-[#1A1A19]"
                  placeholder="Find a person, organisation or project…  /"
                  value={findText}
                  onChange={(e) => {
                    e.target.setCustomValidity("");
                    setFindText(e.target.value);
                    if (G.nodes.some((n) => n.label === e.target.value)) find(e.target.value); // picked from the list
                  }}
                />
              </label>
              <datalist id="graph-names">
                {G.nodes.map((n) => (
                  <option key={n.id} value={n.label} label={KIND[n.type] + (n.type === "person" ? ` · ${byId.get(n.org!)?.label ?? ""}` : "")} />
                ))}
              </datalist>
            </form>
            <Button variant="outline" onClick={reset} title="Show everything again (Esc)">
              Reset view
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-[#262523] px-5 py-3 sm:px-10">
        {projects.map((n) => {
          const on = !off.has(n.project!);
          const c = colors[n.project!] ?? MUTED;
          return (
            <button
              key={n.id}
              type="button"
              aria-pressed={on}
              title="Show or hide this project"
              onClick={() => {
                const s = new Set(off);
                if (on) s.add(n.project!);
                else s.delete(n.project!);
                setOff(s);
              }}
              className={cn("inline-flex min-h-8 items-center gap-2 rounded-2xl px-2.5 font-mono text-xs", !on && "line-through")}
              style={on ? { background: `${c}29`, color: TINT[c] ?? c } : { background: "#262523", color: "#A8A59D" }}
            >
              <span aria-hidden="true" className="size-2 rounded-full" style={{ background: on ? c : MUTED }} />
              {n.label}
            </button>
          );
        })}
        <span className="grow" />
        <label className="flex items-center gap-2 text-[13px] text-[#CFCBC1]">
          <input type="checkbox" className="size-4 accent-[#FF4F1A]" checked={noGroups} onChange={(e) => setNoGroups(e.target.checked)} />
          hide groups &amp; events
        </label>
        <label className="flex items-center gap-2 text-[13px] text-[#CFCBC1]">
          <input type="checkbox" className="size-4 accent-[#FF4F1A]" checked={sure} onChange={(e) => setSure(e.target.checked)} />
          only confirmed / high confidence
        </label>
      </div>

      <div className="flex flex-wrap">
        <div className="relative h-[calc(100svh-170px)] min-h-[520px] min-w-0 flex-[999_1_520px] bg-[#0E0E0D] bg-[radial-gradient(circle,#2A2927_1px,transparent_1.5px)] bg-[length:24px_24px]">
          {/* cytoscape makes its container position:relative, so size it explicitly rather than with inset-0 */}
          <div ref={box} aria-label="Graph canvas" className="h-full w-full" />
          <div role="group" aria-label="Zoom" className="absolute right-5 bottom-5 flex flex-col gap-px overflow-hidden rounded-md bg-[#34332F]">
            {(
              [
                ["Zoom in", "+", () => zoomBy(cyRef.current, 1.3)],
                ["Zoom out", "−", () => zoomBy(cyRef.current, 1 / 1.3)],
                ["Fit to screen", "⤢", () => cyRef.current && settle(cyRef.current)],
              ] as const
            ).map(([label, sym, fn]) => (
              <button key={label} type="button" aria-label={label} title={label} onClick={fn} className="size-10 bg-[#1A1A19] text-lg text-[#ECEAE4] hover:text-[#FF4F1A]">
                {sym}
              </button>
            ))}
          </div>
          <p className="pointer-events-none absolute bottom-4 left-8 m-0 max-w-[calc(100%-120px)] text-xs text-[#A8A59D]">
            green ring = confirmed by you · dashed = group, event or “sometimes” · / to search, Esc to reset
          </p>
        </div>
        <aside
          aria-label="Details"
          className="box-border flex h-[calc(100svh-170px)] min-h-[520px] min-w-0 flex-[1_1_300px] flex-col gap-4 overflow-auto border-l border-[#262523] p-6"
        >
          <Details
            n={byId.get(panelId) ?? byId.get("me")!}
            G={G}
            byId={byId}
            go={go}
            serverId={id}
            openPlane={(to) => openPlane.mutate(to)}
          />
        </aside>
      </div>
    </>
  );
}

function zoomBy(cy: Core | null, f: number) {
  if (cy) cy.zoom({ level: cy.zoom() * f, renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 } });
}

function Details({
  n,
  G,
  byId,
  go,
  serverId,
  openPlane,
}: {
  n: GNode;
  G: GraphData;
  byId: Map<string, GNode>;
  go: (id: string) => void;
  serverId: string;
  openPlane: (to: string) => void;
}) {
  const rows: [string, ReactNode][] = [];
  const row = (k: string, v: ReactNode) => {
    if (v == null || v === "" || (Array.isArray(v) && !v.length)) return;
    rows.push([k, v]);
  };
  const link = (nid: string, text: string, extra?: string) => (
    <span key={nid}>
      <button type="button" onClick={() => go(nid)} className="text-left text-[#ECEAE4] underline decoration-[#4A4945] underline-offset-2 hover:text-[#FF4F1A]">
        {text}
      </button>
      {extra && <span className="text-[#A8A59D]"> {extra}</span>}
    </span>
  );
  const list = (items: [string, string, string?][]) => (
    <ul className="m-0 flex flex-col gap-0.5 pl-4">
      {items.map(([nid, text, extra]) => (
        <li key={nid}>{link(nid, text, extra)}</li>
      ))}
    </ul>
  );
  const plabel = (slug: string) => byId.get(`project:${slug}`)?.label ?? slug;
  let status: ReactNode = null;

  if (n.type === "person") {
    const org = byId.get(n.org!);
    row("Organisation", org && link(org.id, org.label));
    row(
      "Projects",
      list([
        ...(n.projects ?? []).map((s): [string, string] => [`project:${s}`, plabel(s)]),
        ...(n.hints ?? []).map((s): [string, string, string] => [`project:${s}`, plabel(s), "(sometimes)"]),
      ]),
    );
    status = n.confirmed ? (
      <Chip value="ok" label="confirmed by you" />
    ) : n.confidence ? (
      <Chip value={n.confidence === "high" ? "neutral" : "warn"} label={`confidence: ${n.confidence}`} />
    ) : null;
    for (const [k, v] of Object.entries(n.detail ?? {})) if (k !== "confidence") row(k, v.join(" · "));
    const colleagues = G.nodes.filter((x) => x.type === "person" && x.org === n.org && x.id !== n.id);
    if (colleagues.length && colleagues.length <= 40)
      row(`Also at ${org?.label ?? ""}`, list(colleagues.map((x) => [x.id, x.label, x.confirmed ? "✓" : undefined])));
  } else if (n.type === "org") {
    row("Section", n.subtitle);
    if (n.project) row("Project", link(`project:${n.project}`, plabel(n.project)));
    row(
      `People (${n.size})`,
      list(
        G.nodes
          .filter((x) => x.type === "person" && x.org === n.id)
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((x) => [x.id, x.label, x.confirmed ? "✓" : undefined]),
      ),
    );
  } else if (n.type === "project") {
    const orgs = G.nodes.filter((x) => x.type === "org" && x.project === n.project);
    const people = G.nodes.filter((x) => x.type === "person" && x.projects?.includes(n.project!));
    row("Size", `${orgs.length} organisations · ${people.length} people`);
    if (n.plane_id && G.workspace)
      row(
        "Tasks",
        <button type="button" onClick={() => openPlane(`/${G.workspace}/projects/${n.plane_id}/issues/`)} className="font-mono text-[13px] hover:text-[#FF4F1A]">
          Plane project {n.plane} ↗
        </button>,
      );
    row("Organisations", list(orgs.sort((a, b) => (b.size ?? 0) - (a.size ?? 0)).map((x) => [x.id, `${x.label} (${x.size})`])));
  } else {
    row("Projects", list(G.nodes.filter((x) => x.type === "project").map((x) => [x.id, x.label])));
    for (const [k, v] of Object.entries(n.detail ?? {})) row(k, v.join(" · "));
  }

  return (
    <>
      <div className="flex flex-col gap-1">
        <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">{KIND[n.type]}</span>
        <h2 className="m-0 font-mono text-xl font-bold break-words">{n.label}</h2>
        {status && <span className="self-start">{status}</span>}
      </div>
      <dl className="m-0 grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2.5 text-[13px]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-[#A8A59D] first-letter:uppercase">{k}</dt>
            <dd className="m-0 break-words">{v}</dd>
          </div>
        ))}
      </dl>
      {n.type === "person" && (
        <Link to="/servers/$id/people" params={{ id: serverId }} search={{ q: n.label }} className="font-mono text-[13px] no-underline">
          Open their People card →
        </Link>
      )}
    </>
  );
}

/** ServerPage's header; the page below it is full-bleed (filter strip, then canvas and details edge to edge), as on the canvas. */
function GraphHeader({ actions }: { actions?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4 border-b border-[#262523] px-5 py-5 sm:px-10">
      <div className="flex flex-col gap-1">
        <h1 className="m-0 font-mono text-[26px] font-bold tracking-[-0.02em]">Graph</h1>
        <p className="m-0 text-[13px] text-[#A8A59D]">
          Projects contain organisations, organisations contain people. Lines are people who also belong to another project.
          Click to highlight; double-click to zoom in.
        </p>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

function Graph() {
  return <GraphBody />;
}
